import { getSettings } from './settings';

export interface MeInvoiceConfig {
  baseUrl: string;
  authorization: string;
  clientId: string;
  clientSecret?: string;
  subscriberCode: string;
  subscriberId: string;
  organizationId: string;
  sessionHeaders?: Record<string, string>;
}

export interface MeInvoicePage<T = Record<string, any>> { rows: T[]; total: number }
const clean = (value: unknown) => String(value ?? '').trim();

export function parseMeInvoiceConfig(raw: string): MeInvoiceConfig {
  const value = JSON.parse(raw);
  const config: MeInvoiceConfig = {
    baseUrl: clean(value.baseUrl || value.base_url || 'https://app.meinvoice.vn').replace(/\/+$/, ''),
    authorization: clean(value.authorization || value.Authorization),
    clientId: clean(value.clientId || value.clientid),
    clientSecret: clean(value.clientSecret || value['client-secret']) || undefined,
    subscriberCode: clean(value.subscriberCode || value.subscribercode),
    subscriberId: clean(value.subscriberId),
    organizationId: clean(value.organizationId || value.orgId),
    sessionHeaders: value.sessionHeaders && typeof value.sessionHeaders === 'object' ? value.sessionHeaders : undefined,
  };
  if (!config.authorization || !config.clientId || !config.subscriberCode || !config.subscriberId || !config.organizationId) {
    throw new Error('JSON meInvoice thiếu authorization, clientId, subscriberCode, subscriberId hoặc organizationId.');
  }
  return config;
}

async function loadConfig() {
  const settings = await getSettings();
  if (!settings.meInvoiceConfigJson?.trim()) throw new Error('Chưa cấu hình phiên MISA meInvoice trong Cài đặt.');
  return parseMeInvoiceConfig(settings.meInvoiceConfigJson);
}

async function request(path: string, init?: RequestInit) {
  const config = await loadConfig();
  const root = `${config.baseUrl}/inbot/api/g1/${encodeURIComponent(config.subscriberId)}/${encodeURIComponent(config.organizationId)}`;
  const response = await fetch(`${root}${path}`, {
    ...init,
    headers: {
      Accept: 'application/json, text/plain, */*',
      'Content-Type': 'application/json',
      Authorization: config.authorization.startsWith('Bearer ') ? config.authorization : `Bearer ${config.authorization}`,
      clientid: config.clientId,
      ...(config.clientSecret ? { 'client-secret': config.clientSecret } : {}),
      subscribercode: config.subscriberCode,
      ...(config.sessionHeaders || {}),
      ...(init?.headers || {}),
    },
  });
  if (!response.ok) throw new Error(`MISA meInvoice trả về HTTP ${response.status}. Phiên đăng nhập có thể đã hết hạn.`);
  const text = await response.text();
  if (!text) return null;
  try { return JSON.parse(text); } catch { throw new Error('MISA meInvoice trả về dữ liệu không phải JSON.'); }
}

function unwrap(value: any): any {
  let current = value;
  for (let index = 0; index < 4; index++) {
    const next = current?.Data ?? current?.data ?? current?.Result ?? current?.result;
    if (next == null || next === current) break;
    current = next;
    if (typeof current === 'string') {
      try { current = JSON.parse(current); } catch { break; }
    }
  }
  return current;
}

export function extractMeInvoiceRows(json: any): Record<string, any>[] {
  const data = unwrap(json);
  if (Array.isArray(data)) return data;
  const rows = [data?.Items, data?.items, data?.Rows, data?.rows, data?.PageData, data?.pageData, data?.DataList, data?.dataList].find(Array.isArray);
  if (!rows) throw new Error('Response meInvoice không có danh sách dữ liệu đã biết.');
  return rows;
}

export function extractMeInvoiceTotal(json: any, fallback = 0): number {
  const data = unwrap(json);
  if ((typeof data === 'number' || typeof data === 'string') && Number.isFinite(Number(data))) return Number(data);
  const envelopes = [json, json?.Data, json?.data, json?.Result, json?.result, data];
  const found = envelopes.flatMap((value) => [
    value?.Total, value?.total, value?.TotalCount, value?.totalCount, value?.Count, value?.count,
  ])
    .map(Number).find(Number.isFinite);
  return found ?? fallback;
}

async function fetchAll(
  path: string,
  payload: Record<string, any>,
  pageSize: number,
  totalPath?: string,
): Promise<MeInvoicePage> {
  const rows: Record<string, any>[] = [];
  let total = -1;
  if (totalPath) {
    const totalJson = await request(totalPath, {
      method: 'POST', body: JSON.stringify({ ...payload, skip: 0, take: pageSize }),
    });
    total = extractMeInvoiceTotal(totalJson, -1);
  }
  let skip = 0;
  for (let page = 0; page < 1000; page++) {
    const json = await request(path, { method: 'POST', body: JSON.stringify({ ...payload, skip, take: pageSize }) });
    const pageRows = extractMeInvoiceRows(json);
    rows.push(...pageRows);
    const responseTotal = extractMeInvoiceTotal(json, -1);
    if (responseTotal >= 0) total = Math.max(total, responseTotal);
    if (!pageRows.length || (total >= 0 && rows.length >= total) || (total < 0 && pageRows.length < pageSize)) break;
    skip += pageRows.length;
  }
  if (total >= 0 && rows.length < total) throw new Error('Đồng bộ meInvoice vượt giới hạn 1000 trang.');
  return { rows, total: Math.max(total, rows.length) };
}

export async function fetchAllMeInvoices(options?: { fromISO?: string; toISO?: string; pageSize?: number }) {
  const from = (options?.fromISO || '2020-01-01').slice(0, 10);
  const to = (options?.toISO || new Date().toISOString()).slice(0, 10);
  return fetchAll('/invoices/v3', {
    Conditions: { FilterType: 1, Conditions: [] },
    FilterDate: { DateFilterType: 3, Operator: 'between', FromDate: `${from}T00:00:00`, ToDate: `${to}T23:59:59` },
    TextSearch: null,
  }, options?.pageSize || 100, '/invoices/totalv3');
}

export async function fetchAllMeSuppliers(pageSize = 100): Promise<MeInvoicePage> {
  const rows: Record<string, any>[] = [];
  let total = -1;
  let skip = 0;
  for (let page = 0; page < 1000; page++) {
    const query = new URLSearchParams({
      skip: String(skip),
      take: String(pageSize),
      searchText: '',
      ActiveStatus: '999',
      RiskStatus: '0',
      Sort: '',
    });
    const json = await request(`/supplier?${query.toString()}`);
    const pageRows = extractMeInvoiceRows(json);
    rows.push(...pageRows);
    const responseTotal = extractMeInvoiceTotal(json, -1);
    if (responseTotal >= 0) total = Math.max(total, responseTotal);
    if (!pageRows.length || (total >= 0 && rows.length >= total) || (total < 0 && pageRows.length < pageSize)) break;
    skip += pageRows.length;
  }
  if (total >= 0 && rows.length < total) throw new Error('Đồng bộ nhà cung cấp meInvoice vượt giới hạn 1000 trang.');
  return { rows, total: Math.max(total, rows.length) };
}

const entries = (row: Record<string, any>) => Object.entries(row).map(([key, value]) => ({ key: key.toLowerCase().replace(/[^a-z0-9]/g, ''), value }));
const pick = (row: Record<string, any>, names: string[]) => {
  const source = entries(row);
  for (const name of names.map((item) => item.toLowerCase().replace(/[^a-z0-9]/g, ''))) {
    const entry = source.find((item) => item.key === name) || source.find((item) => item.key.endsWith(name));
    if (entry?.value != null) return entry.value;
  }
};
const toDate = (value: unknown) => {
  const raw = clean(value);
  const iso = raw.match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (iso) return `${iso[1]}-${iso[2]}-${iso[3]}`;
  const local = raw.match(/^(\d{1,2})[/-](\d{1,2})[/-](\d{4})/);
  return local ? `${local[3]}-${local[2].padStart(2, '0')}-${local[1].padStart(2, '0')}` : '';
};
const toNumber = (value: unknown) => {
  if (typeof value === 'number') return Number.isFinite(value) ? Math.round(value) : 0;
  const raw = clean(value).replace(/\s/g, '');
  const result = Number(/^-?\d{1,3}(?:[.,]\d{3})+$/.test(raw) ? raw.replace(/[.,]/g, '') : raw.replace(/[^\d.-]/g, ''));
  return Number.isFinite(result) ? Math.round(result) : 0;
};

export function mapMeInvoiceRow(row: Record<string, any>) {
  return {
    meInvoiceId: clean(pick(row, ['Id', 'InvoiceId'])),
    invoiceNumber: clean(pick(row, ['InvoiceNo', 'InvoiceNumber', 'InvNo', 'No'])),
    supplierName: clean(pick(row, ['SellerName', 'SupplierName', 'CompanyName', 'AccountObjectName', 'PartnerName'])),
    taxCode: clean(pick(row, ['SellerTaxCode', 'SupplierTaxCode', 'CompanyTaxCode', 'TaxCode'])),
    invoiceDate: toDate(pick(row, ['InvoiceDate', 'InvDate', 'IssuedDate', 'Date'])),
    amount: toNumber(pick(row, ['TotalAmountWithVAT', 'TotalPaymentAmount', 'TotalAmount', 'Amount'])),
    note: 'Lấy tự động từ MISA meInvoice',
  };
}

export async function fetchMeInvoiceDetail(meInvoiceId: string) {
  if (!clean(meInvoiceId)) throw new Error('Hóa đơn chưa có định danh meInvoice để lấy chi tiết.');
  return request(`/invoices/${encodeURIComponent(meInvoiceId)}`);
}

const detailOtherValue = (items: unknown, field: string) => {
  if (!Array.isArray(items)) return '';
  const target = field.toLowerCase();
  const item = items.find((entry) => clean(entry?.TTruong ?? entry?.tTruong ?? entry?.Field ?? entry?.field).toLowerCase() === target);
  return clean(item?.DLieu ?? item?.dLieu ?? item?.Value ?? item?.value);
};

export function extractMeInvoiceDetail(json: any) {
  const detail = unwrap(json);
  return {
    meInvoiceId: clean(detail?.Id ?? detail?.InvoiceId),
    invoiceNumber: clean(detail?.InvoiceNo ?? detail?.InvoiceNumber),
    invoiceDate: toDate(detail?.InvoiceDate),
    supplierName: clean(detail?.SellerName),
    taxCode: clean(detail?.SellerTaxCode),
    amount: toNumber(detail?.TotalAmount ?? detail?.TotalAmountXML),
    bankAccount: detailOtherValue(detail?.SellerOthers, 'SellerBankAccount'),
    bankName: detailOtherValue(detail?.SellerOthers, 'SellerBankName'),
  };
}

export function mapMeSupplierRow(row: Record<string, any>) {
  // meInvoice supplier rows use CompanyName for the logged-in company and Name for the supplier.
  const name = clean(pick(row, ['Name', 'SupplierName', 'AccountObjectName', 'CompanyName']));
  const accountNumber = clean(pick(row, ['BankAccount', 'BankAccountNumber', 'AccountNumber']));
  const bankName = clean(pick(row, ['BankName']));
  const accountHolder = clean(pick(row, ['AccountHolder', 'BankAccountName'])) || name;
  return {
    name,
    taxCode: clean(pick(row, ['TaxCode', 'CompanyTaxCode', 'SupplierTaxCode'])),
    phone: clean(pick(row, ['Phone', 'Tel', 'Mobile'])),
    address: clean(pick(row, ['Address', 'CompanyAddress'])),
    bankName, accountNumber, accountHolder,
    bankAccounts: accountNumber ? [{ accountNumber, bankName, accountHolder, isPrimary: true as const }] : [],
    note: 'Lấy tự động từ MISA meInvoice',
  };
}

export async function testMeInvoiceConnection() {
  const page = await fetchAllMeInvoices({ pageSize: 20 });
  return { count: page.rows.length, total: page.total };
}
